import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";

const CHART_COLORS = {
  text: "#5a6577",
  grid: "rgba(26, 43, 95, 0.1)",
  legend: "#1a2b5f",
};

export default function AnalyticsCharts({ chartData, timeRange }) {
  const [isMobile, setIsMobile] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  useEffect(() => {
    const labels = (chartData?.labels || []).map((label) => {
      // Extract just the date part (YYYY-MM-DD) from the label
      if (typeof label === "string") {
        // If it's already in YYYY-MM-DD format, use it
        if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
          return label;
        }
        // If it contains a date part, extract it
        const dateMatch = label.match(/^\d{4}-\d{2}-\d{2}/);
        if (dateMatch) {
          return dateMatch[0];
        }
        // Try to parse and format
        const date = new Date(label);
        if (!Number.isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        }
      }
      return label != null ? String(label) : "";
    });
    const expectedApr = chartData?.expectedApr || [];
    const impliedApr = chartData?.impliedApr || [];
    const apy90Day = chartData?.apy90Day || [];
    const apy30Day = chartData?.apy30Day || [];


    const canvas = document.getElementById("analyticsChart");
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    if (chartRef.current) {
      const chart = chartRef.current;
      chart.data.labels = labels;
      if (chart.data.datasets[0]) {
        chart.data.datasets[0].data = expectedApr;
      }
      if (chart.data.datasets[1]) {
        chart.data.datasets[1].data = impliedApr;
      }
      if (chart.data.datasets[2]) {
        chart.data.datasets[2].data = apy90Day;
      }
      if (chart.data.datasets[3]) {
        chart.data.datasets[3].data = apy30Day;
      }
      if (chart.options?.scales?.x?.ticks) {
        chart.options.scales.x.ticks.maxRotation = isMobile ? 25 : 0;
        chart.options.scales.x.ticks.minRotation = isMobile ? 25 : 0;
        chart.options.scales.x.ticks.callback = function(value) {
          const label = this.getLabelForValue(value);
          if (typeof label === "string") {
            const dateMatch = label.match(/^\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              return isMobile ? dateMatch[0].slice(0, 7) : dateMatch[0];
            }
            if (/^\d{4}-\d{2}$/.test(label)) {
              return label;
            }
            const date = new Date(label);
            if (!Number.isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              return isMobile ? `${year}-${month}` : `${year}-${month}-${String(date.getDate()).padStart(2, "0")}`;
            }
          }
          return label;
        };
      }
      if (chart.options?.scales?.x) {
        chart.options.scales.x.reverse = false;
      }
      chart.update("none");
      return;
    }

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Expected APR",
            data: expectedApr,
            borderColor: "#5BA4E6",
            backgroundColor: "rgba(91, 164, 230, 0.1)",
            fill: true,
            tension: 0.4,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHitRadius: 8,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: "#5BA4E6",
          },
          {
            label: "Implied APR",
            data: impliedApr,
            borderColor: "#1a2b5f",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.4,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHitRadius: 8,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: "#1a2b5f",
          },
          {
            label: "90-Day APY",
            data: apy90Day,
            borderColor: "#2d8a5f",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.4,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHitRadius: 8,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: "#2d8a5f",
          },
          {
            label: "30-Day APY",
            data: apy30Day,
            borderColor: "#c9a227",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.4,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHitRadius: 8,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: "#c9a227",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          axis: "x",
          intersect: false,
        },
        hover: {
          mode: "index",
          axis: "x",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            displayColors: true,
            padding: 12,
            mode: "index",
            intersect: false,
            titleFont: {
              family: "'Source Serif 4', serif",
              size: 12,
              weight: "600",
            },
            bodyFont: {
              family: "'Source Serif 4', serif",
              size: 11,
              weight: "400",
            },
            titleMarginBottom: 8,
            callbacks: {
              title: (items) => items?.[0]?.label || "",
              label: (context) => {
                const value = Number(context.parsed?.y);
                if (!Number.isFinite(value)) {
                  return "";
                }
                return `${context.dataset.label}: ${value.toFixed(2)}%`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: CHART_COLORS.grid },
            reverse: false,
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              maxTicksLimit: 12,
              autoSkip: true,
              maxRotation: isMobile ? 25 : 0,
              minRotation: isMobile ? 25 : 0,
              callback: function(value, index, ticks) {
                const label = this.getLabelForValue(value);
                // Extract just the date part (YYYY-MM-DD) if it's a full timestamp
                if (typeof label === "string") {
                  // If it's already in YYYY-MM-DD format, use it
                  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                    return isMobile ? label.slice(0, 7) : label;
                  }
                  // If it contains a date part, extract it
                  const dateMatch = label.match(/^\d{4}-\d{2}-\d{2}/);
                  if (dateMatch) {
                    return isMobile ? dateMatch[0].slice(0, 7) : dateMatch[0];
                  }
                  // Try to parse and format from a full date string
                  const date = new Date(label);
                  if (!Number.isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    if (isMobile) {
                      return `${year}-${month}`;
                    }
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${year}-${month}-${day}`;
                  }
                }
                return label;
              },
            },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    });
  }, [chartData, timeRange, isMobile]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  return <canvas id="analyticsChart"></canvas>;
}
